import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  Box,
  Card,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Skeleton,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Add, RemoveCircleOutline } from '@mui/icons-material';

import { UserRoles } from '@bandido/shared/types';

import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';
import { BackButton } from '@frontend/features/common/components/BackButton';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { Table, usePagination } from '@frontend/features/common/components/Table';
import { Link } from '@frontend/features/common/components/Link';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';

import { useGetUserAgencies, useGetUserById } from '@frontend/features/users/queries';

import { useGetTeamsByUser } from '@frontend/features/teams/queries';
import { LinkTeamToUser } from '@frontend/features/teams/components/LinkTeamToUser';

import { routes } from '../routes';
import { UserAgency } from '@frontend/features/users/components/UserAgency';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useGetAllAgencies } from '@frontend/features/agencies/queries';

export const UserView = () => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const { myProfile, ability } = useAuth();
  const theme = useTheme();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [isOpenLinkTeam, setIsOpenLinkTeam] = useState(false);
  const toggleOpenLinkTeam = () => setIsOpenLinkTeam((prev) => !prev);
  const pagination = usePagination();
  const rolesOptions = Object.values(UserRoles);

  const [search, setSearch] = useDebouncedState('', 300);

  const { data: teams, isLoading: isLoadingTeams } = useGetTeamsByUser({
    userId: id,
    search: search,
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
  });

  const isAdmin = myProfile?.roles?.split(',').includes(UserRoles.ADMINS);
  const isOperator = myProfile?.roles?.split(',').includes(UserRoles.OPERATORS);

  const { data } = useGetUserById({
    id,
  });

  const { data: selectedAgencies} = useGetUserAgencies({userId: id});

  const { data: agencies } = useGetAllAgencies({
    pageSize: 10000,
    currentPage: 1,
    search: '',
  });

  const handleToggleActiveAgency = (agencyId: number, isActive: boolean) => () => {
    const action = isActive ? 'disable': 'enable';
    const performAction = async () => {
      try  {
        await client.patch(`user/${id}/agencies/${agencyId}/${action}`);
        queryClient.invalidateQueries([QueryKeys.getUserAgencies, id]);
      } catch(_e) {
        addToast({
          message: t('agencyAccess.saveError'),
          color: 'error',
        });
      }
    };   
    performAction();
  };

  const handleUpdateRoles = (value: UserRoles) => async () => {
    try {
      let roles: string[] = [];
      const dataRoles = data?.roles?.split(',') || [];
      if (dataRoles.includes(value)) {
        roles = dataRoles.filter((rol) => rol !== value);
      } else {
        roles = [...dataRoles, value];
      }
      await client.patch(`user/${id}/profile/roles`, {
        roles,
      });
      queryClient.invalidateQueries([QueryKeys.getUserById, id]);
    } catch (e) {
      console.log('error', e);
    }
  };

  const handleRemoveFromTeam = async (teamId: number) => {
    try {
      await client.delete(`/team/${teamId}/user/${id}`);
      addToast({
        message: t('teams.linkUserToTeam.removed'),
        color: 'success',
      });
      queryClient.invalidateQueries([QueryKeys.getUsersByTeam, id, pagination.page, pagination.rowsPerPage, search]);
    } catch (e) {
      addToast({
        message: t('teams.linkUserToTeam.removedFailed'),
        color: 'error',
      });
      console.log(e);
    }
  };

  const canManageUsers = ability?.can('manage', 'Users');

  return (
    <Box display="flex" gap={3} flexDirection="column" p={theme.spacing(4)}>
      <BackButton backRoute={routes.dashboard.users} />

      <Box>
        <Typography variant="h6" color={theme.palette.text.primary}>
          {data?.firstName} {data?.lastName}
        </Typography>
        <Typography variant="subtitle1" align="left" color={theme.palette.text.disabled}>
          {data?.email}
        </Typography>
      </Box>

      <Grid container>
        <Grid item xs={12} sm={canManageUsers ? 8 : 12}>
          {canManageUsers && <Card
            sx={{
              p: theme.spacing(2),
              boxShadow: 'none',
              borderRadius: theme.spacing(3),
            }}
            elevation={4}
          >
            <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
              <Typography
                variant="subtitle1"
                align="left"
                color={theme.palette.text.primary}
                sx={{
                  flexGrow: 1,
                }}
              >
                {teams?.count} {t('users.view.teams')}
              </Typography>

              <SearchInput placeholder="Buscar" onSearch={setSearch} />
              <ToggleButtonGroup
                exclusive
                sx={{
                  height: theme.spacing(5),
                  '& button': {
                    padding: theme.spacing(1),
                    width: theme.spacing(5),
                  },
                }}
              >
                {/* TODO: Agregar link user to team */}
                <ToggleButton value="add" onClick={toggleOpenLinkTeam}>
                  <Add />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {id && data?.agencyId && (
              <LinkTeamToUser
                agencyId={data?.agencyId}
                isOpen={isOpenLinkTeam}
                onClose={toggleOpenLinkTeam}
                userId={id}
                queryKey={[QueryKeys.getUsersByTeam, id, pagination.page, pagination.rowsPerPage, search]}
              />
            )}

            {teams?.count !== undefined && teams.count > 0 && (
              <Table
                columns={[
                  {
                    id: 'color',
                    label: t('teams.table.color'),
                    align: 'left',
                    format: (value) => (
                      <Box
                        component="div"
                        sx={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: value,
                        }}
                      />
                    ),
                  },
                  {
                    id: 'name',
                    label: t('teams.table.name'),
                    align: 'left',
                  },
                  {
                    id: 'agencyName',
                    label: t('teams.table.agency'),
                    align: 'left',
                  },
                  {
                    id: 'actions',
                    label: '',
                    align: 'right',
                  },
                ]}
                data={{
                  key: 'id',
                  items: teams.items.map((item) => ({
                    ...item,
                    name: (
                      <Link
                        to={`${routes.dashboard.agencyView.root.replace(
                          ':id',
                          String(item.agencyId),
                        )}/${routes.dashboard.agencyView.team.replace(':teamId', String(item.id))}`}
                      >
                        {item.name}
                      </Link>
                    ),
                    agencyName: (
                      <Link to={routes.dashboard.agencyView.root.replace(':id', String(item.agencyId))}>
                        {item.agencyName}
                      </Link>
                    ),
                    actions: (
                      <Tooltip title={t('teams.linkUserToTeam.removedTooltip')} placement="top">
                        <IconButton onClick={() => handleRemoveFromTeam(item.id)}>
                          <RemoveCircleOutline color="error" />
                        </IconButton>
                      </Tooltip>
                    ),
                  })),
                  totalCount: teams.count,
                }}
                pagination={pagination}
                stickyHeader
              />
            )}

            {isLoadingTeams && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
          </Card>}

          {/* TODO: Aca hay q agregar un listado de casos listando los casos del user, pero solo dentro del universo de casos q el token puede ver. (nunca mas voy a entender esto jajaja) */}
        </Grid>

        {canManageUsers && <Grid item xs={12} sm={4}>
          <Box display="flex" flexDirection="column" gap={2}>
            {data?.id && <UserAgency userId={data.id} clientId={data.clientId} selectedAgency={data?.agencyId} />}

            <Card
              sx={{
                p: theme.spacing(0),
                boxShadow: 'none',
                borderRadius: theme.spacing(3),
              }}
              elevation={4}
            >
              <List subheader={<ListSubheader>Accesso a Agencias</ListSubheader>}></List>
              {agencies?.items.map((agency) => (
                <ListItem key={agency.id}>
                  <ListItemText primary={agency.name} />
                  <Switch
                    edge="end"
                    onChange={handleToggleActiveAgency(agency.id, !!selectedAgencies?.includes(agency.id))}
                    checked={selectedAgencies?.includes(agency.id)}
                  />
                </ListItem>
              ))}
            </Card>
            <Card
              sx={{
                p: theme.spacing(0),
                boxShadow: 'none',
                borderRadius: theme.spacing(3),
              }}
              elevation={4}
            >
              <List subheader={<ListSubheader>{t('roles.title')}</ListSubheader>}>
                {rolesOptions.map((role) => {
                  if (role === UserRoles.ADMINS && !isAdmin) {
                    return null;
                  }

                  if (role === UserRoles.OPERATORS && !(isAdmin || isOperator)) {
                    return null;
                  }

                  return (
                    role !== UserRoles.REGISTERED && (
                      <ListItem key={role}>
                        <ListItemText primary={t(`roles.${role}`)} />
                        <Switch
                          edge="end"
                          onChange={handleUpdateRoles(role)}
                          checked={data?.roles?.split(',').includes(role) ?? false}
                        />
                      </ListItem>
                    )
                  );
                })}
              </List>
            </Card>
          </Box>
        </Grid>}

      </Grid>
    </Box>
  );
};
